// Small shared building blocks for the reporting dashboards.

import React from "react";
import { ArrowDownRight, ArrowUpRight, Minus, ShieldAlert } from "lucide-react";
import { cn } from "../../../lib/utils";
import { Card, CardContent } from "../../../ui/card";

export const WINDOW_OPTIONS = [7, 30, 90];

/** Pill button used across reporting filters. */
export const FilterChip = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      "px-2.5 py-0.5 rounded-full border text-xs font-medium transition-colors",
      active
        ? "border-sky-500 bg-sky-50 text-sky-700"
        : "border-border bg-background text-muted-foreground hover:text-foreground"
    )}
  >
    {children}
  </button>
);

/** 7 / 30 / 90-day window selector. */
export const WindowToggle = ({ value, onChange }) => (
  <div className="inline-flex rounded-md border bg-muted/40 p-0.5">
    {WINDOW_OPTIONS.map((days) => (
      <button
        key={days}
        type="button"
        onClick={() => onChange(days)}
        className={cn(
          "px-3 py-1 text-xs font-medium rounded-[5px] transition-colors",
          value === days
            ? "bg-background shadow-sm text-foreground"
            : "text-muted-foreground hover:text-foreground"
        )}
      >
        {days}d
      </button>
    ))}
  </div>
);

/** Up/down arrow vs the previous equivalent window. */
export const TrendDelta = ({ current, previous, downIsGood = false }) => {
  if (
    current == null ||
    previous == null ||
    Number.isNaN(current) ||
    Number.isNaN(previous)
  ) {
    return null;
  }
  if (previous === 0 && current === 0) {
    return <Minus className="h-3.5 w-3.5 text-muted-foreground inline" />;
  }
  const delta = previous === 0 ? 1 : (current - previous) / Math.abs(previous);
  const up = delta > 0.005;
  const down = delta < -0.005;
  const good = (up && !downIsGood) || (down && downIsGood);
  const Icon = up ? ArrowUpRight : down ? ArrowDownRight : Minus;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-0.5 text-xs font-medium",
        up || down
          ? good
            ? "text-emerald-600"
            : "text-red-500"
          : "text-muted-foreground"
      )}
    >
      <Icon className="h-3.5 w-3.5" />
      {Math.abs(delta * 100).toFixed(0)}%
    </span>
  );
};

/** KPI card: label, big value, optional sub-line and trend. */
export const StatCard = ({ label, value, sub, trend }) => (
  <Card className="border-slate-200/80 shadow-[0_1px_3px_rgba(15,23,42,0.06),0_4px_12px_rgba(15,23,42,0.04)]">
    <CardContent className="p-4">
      <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
        {label}
      </div>
      <div className="mt-1 flex items-baseline gap-2">
        <span className="text-2xl font-semibold tabular-nums">
          {value ?? "—"}
        </span>
        {trend}
      </div>
      {sub ? (
        <div className="mt-0.5 text-xs text-muted-foreground">{sub}</div>
      ) : null}
    </CardContent>
  </Card>
);

/** Horizontal ranked bars (Top Issues, Benefits Recommended). */
export const RankedBars = ({ items, color = "bg-primary/70", emptyLabel }) => {
  if (!items?.length) {
    return (
      <p className="text-sm text-muted-foreground py-4">
        {emptyLabel || "No data in this window."}
      </p>
    );
  }
  const max = Math.max(...items.map((item) => item.count), 1);
  return (
    <div className="space-y-2">
      {items.map((item) => (
        <div key={item.label} className="flex items-center gap-3">
          <div className="w-44 shrink-0 text-sm truncate" title={item.label}>
            {item.label}
          </div>
          <div className="flex-1 h-5 rounded bg-muted/50 overflow-hidden">
            <div
              className={cn("h-full rounded", color)}
              style={{ width: `${Math.max((item.count / max) * 100, 2)}%` }}
            />
          </div>
          <div className="w-12 shrink-0 text-right text-sm tabular-nums">
            {item.count}
          </div>
          {item.suffix ? (
            <div className="w-20 shrink-0 text-right text-xs text-muted-foreground">
              {item.suffix}
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
};

/** Adoption funnel steps with conversion percentages. */
export const FunnelSteps = ({ steps }) => {
  const base = steps?.[0]?.count || 0;
  return (
    <div className="space-y-2">
      {(steps || []).map((step, index) => {
        const pct = base ? Math.round((step.count / base) * 100) : 0;
        return (
          <div key={step.label} className="flex items-center gap-3">
            <div className="w-48 shrink-0 text-sm">{step.label}</div>
            <div className="flex-1 h-6 rounded bg-muted/50 overflow-hidden">
              <div
                className="h-full rounded bg-sky-500/70"
                style={{ width: `${Math.max(pct, base ? 2 : 0)}%` }}
              />
            </div>
            <div className="w-14 shrink-0 text-right text-sm tabular-nums">
              {step.count}
            </div>
            <div className="w-12 shrink-0 text-right text-xs text-muted-foreground">
              {index === 0 ? "100%" : `${pct}%`}
            </div>
          </div>
        );
      })}
    </div>
  );
};

/** Shown in place of suppressed sections (small-n privacy rules). */
export const SuppressionNotice = ({ children }) => (
  <div className="flex items-start gap-2 rounded-md border border-amber-300/60 bg-amber-50 px-3 py-2.5 text-sm text-amber-800">
    <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" />
    <span>{children}</span>
  </div>
);

export const SectionHeader = ({ title, hint }) => (
  <div className="flex items-baseline justify-between gap-2">
    <h2 className="text-sm font-semibold tracking-tight">{title}</h2>
    {hint ? (
      <span className="text-xs text-muted-foreground">{hint}</span>
    ) : null}
  </div>
);

export const fmtSeconds = (seconds) => {
  if (seconds == null) return "—";
  if (seconds < 60) return `${Math.round(seconds)}s`;
  return `${(seconds / 60).toFixed(1)}m`;
};

export const fmtMs = (ms) => {
  if (ms == null) return "—";
  return ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${Math.round(ms)}ms`;
};
